// Format a date as relative time (e.g., "5 min ago", "2 hours ago", "3 days ago")
export const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears >= 1) {
    return diffYears === 1 ? 'vor 1 Jahr' : `vor ${diffYears} Jahren`;
  }
  if (diffWeeks >= 1) {
    return diffWeeks === 1 ? 'vor 1 Woche' : `vor ${diffWeeks} Wochen`;
  }
  if (diffDays >= 1) {
    return diffDays === 1 ? 'vor 1 Tag' : `vor ${diffDays} Tagen`;
  }
  if (diffHours >= 1) {
    return diffHours === 1 ? 'vor 1 Stunde' : `vor ${diffHours} Stunden`;
  }
  if (diffMinutes >= 1) {
    return diffMinutes === 1 ? 'vor 1 Minute' : `vor ${diffMinutes} Minuten`;
  }
  if (diffSeconds >= 1) {
    return diffSeconds === 1 ? 'vor 1 Sekunde' : `vor ${diffSeconds} Sekunden`;
  }
  return 'gerade eben';
};

export const formatDate = (dateString: string, includeSeconds: boolean = false): string => {
  const date = new Date(dateString);
  return date.toLocaleString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: includeSeconds ? '2-digit' : undefined,
    timeZone: 'Europe/Berlin'
  });
};

/**
 * Formats a date string to a German date string (DD.MM.YYYY) without time.
 */
export const formatDateOnly = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Europe/Berlin'
  });
};

export const shortObjectID = (id: string): string => {
  return id.slice(-8);
};

// Format duration in seconds to a human readable string (e.g., "5:23" or "1:05:23")
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// Format duration in seconds to a human readable string (e.g., "5h 23min")
export const formatLongDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  } else if (minutes > 0) {
    return `${minutes}min`;
  } else {
    return `${secs}s`;
  }
};

// Import currency types
import type { CurrencyCent, USDCent, RFCDate } from '@/models/types';
import type { Seconds } from '@/models/base-types';

// Time-of-day helpers for trading hours (stored as seconds since midnight)

/**
 * Convert seconds since midnight to time string (HH:MM)
 * @param seconds Seconds since midnight (0-86400)
 * @returns Time string in HH:MM format
 */
export const secondsToTimeString = (seconds: Seconds): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Convert time string (HH:MM) to seconds since midnight
 * @param time Time string in HH:MM format
 * @returns Seconds since midnight
 */
export const timeStringToSeconds = (time: string): Seconds => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 3600 + minutes * 60;
};

export const formatCurrency = (amount: number|null|undefined, currency: string|null|undefined): string => {
  if (amount === null || amount === undefined || currency === null || currency === undefined) {
    console.warn('formatCurrency called with invalid values:', { amount, currency });
    return 'N/A';
  }

  if (isNaN(amount)) {
    console.warn('formatCurrency called with NaN amount:', amount);
    return 'N/A';
  }

  try {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  } catch (error) {
    console.warn('formatCurrency error:', error);
    // Fallback if currency is not supported
    return `${amount.toFixed(2)} ${currency || 'EUR'}`;
  }
};

// Helper function to format currency cent values for display (divides by 100)
export const formatCurrencyCentValue = (cents: CurrencyCent|null|undefined, currency: string|null|undefined): string => {
  if (cents === null || cents === undefined) {
    return 'N/A';
  }
  return formatCurrency(cents / 100, currency);
};

// Helper function to format USD cent values for display (divides by 100)
export const formatUSDCentValue = (cents: USDCent|null|undefined): string => {
  if (cents === null || cents === undefined) {
    return 'N/A';
  }
  return formatCurrency(cents / 100, 'USD');
};

// Helper function to convert form input to currency cents (multiplies by 100 and rounds)
export const toCurrencyCentValue = (value: number): CurrencyCent => {
  return Math.round(value * 100);
};

// Helper function to convert form input to USD cents (multiplies by 100 and rounds)
export const toUSDCentValue = (value: number): USDCent => {
  return Math.round(value * 100);
};

// Helper function to calculate premium as percentage of strike price
// Used for Core and Earnings recommendations to show premium % alongside dollar amount
// Note: 1 option contract = 100 shares, premium is for the full contract, strike is per share
export const calculatePremiumPercentage = (premium: USDCent|null|undefined, strikePrice: CurrencyCent|null|undefined): number|null => {
  if (premium === null || premium === undefined || strikePrice === null || strikePrice === undefined) {
    return null;
  }

  if (strikePrice === 0) {
    return null;
  }

  // Premium is for 100 shares (1 contract), strike price is per share
  // Formula: (premium / strikePrice) / 100
  // This accounts for the fact that 1 contract = 100 shares
  return premium / (strikePrice * 100);
};

// Helper function to format premium with both dollar amount and percentage
export const formatPremiumWithPercentage = (premium: USDCent|null|undefined, strikePrice: CurrencyCent|null|undefined): string => {
  const dollarAmount = formatUSDCentValue(premium);
  const percentage = calculatePremiumPercentage(premium, strikePrice);

  if (percentage === null) {
    return dollarAmount;
  }

  return `${dollarAmount} (${(percentage * 100).toFixed(2)}%)`;
};

// Helper function to format RFCDate for display
export const formatRFCDate = (rfcDate: RFCDate|null|undefined): string => {
  if (!rfcDate) {
    return 'N/A';
  }
  // Convert to Date and format
  const date = new Date(rfcDate + 'T00:00:00.000Z');
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// Helper function to convert Date to RFCDate format (yyyy-MM-dd)
// Uses the local calendar date - toISOString() would return the UTC date, which is
// the previous day for CET/CEST between midnight and 01:00/02:00.
export const dateToRFCDateValue = (date: Date): RFCDate => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

// Helper function to convert form input (yyyy-MM-dd string) to RFCDate
export const toRFCDateValue = (dateString: string): RFCDate => {
  return dateString; // Already in yyyy-MM-dd format
};

// Helper function to convert RFCDate to RFC3339Nano timestamp for API calls
export const rfcDateToTimestamp = (rfcDate: RFCDate, endOfDay: boolean = false): string => {
  if (endOfDay) {
    // For end dates, use end of day (23:59:59.999999999)
    return rfcDate + 'T23:59:59.999999999Z';
  } else {
    // For start dates, use start of day (00:00:00.000000000)
    return rfcDate + 'T00:00:00.000000000Z';
  }
};

// SwingTrade and LongTermTrade helper functions
import type { Percentage, SwingTradeAdjustement, LongTermAdjustement } from '@/models';

/**
 * Format a delta value with sign and percentage
 * @param delta The delta value (0-1 range)
 * @returns Formatted string like "+25.00%" or "-15.50%"
 */
export const formatDelta = (delta: Percentage): string => {
  const sign = delta >= 0 ? '+' : '';
  return `${sign}${(delta * 100).toFixed(2)}%`;
};

/**
 * Calculate the running position up to a specific adjustment index
 * @param adjustements Array of adjustments
 * @param upToIndex Index to calculate up to (inclusive)
 * @returns Running position as Percentage
 */
export const calculateRunningPosition = (adjustements: (SwingTradeAdjustement | LongTermAdjustement)[], upToIndex: number): Percentage => {
  return adjustements
    .slice(0, upToIndex + 1)
    .reduce((sum, adj) => sum + ('delta' in adj ? adj.delta : 0), 0);
};

/**
 * Format percentage value (0-1 range) as readable percentage
 * @param value The percentage value (0-1 range)
 * @returns Formatted string like "25,00%"
 */
export const formatPercentage = (value: Percentage): string => {
  return `${(value * 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
};

// Core/Earnings recommendation status helpers
export type OpenStatusType = 'GEHT_DURCH' | 'WIRD_KNAPP' | 'WIRD_EINGEBUCHT';

/**
 * Calculate the display status for OPEN Core/Earnings recommendations
 * Based on winLossDelta value from backend:
 * - GEHT_DURCH: winLossDelta < 0.99 (goes through - good)
 * - WIRD_KNAPP: 0.99 ≤ winLossDelta ≤ 1.01 (close/risky)
 * - WIRD_EINGEBUCHT: winLossDelta > 1.01 (will be assigned - bad)
 *
 * @param winLossDelta Win/Loss delta value
 * @returns Status type or null if winLossDelta is unavailable
 */
export const calculateOpenStatus = (
  winLossDelta: number | null | undefined
): OpenStatusType | null => {
  if (winLossDelta === null || winLossDelta === undefined) {
    return null;
  }

  // winLossDelta < 0.99 → "durchgegangen" (goes through)
  if (winLossDelta < 0.99) {
    return 'GEHT_DURCH';
  }
  // 0.99 ≤ winLossDelta ≤ 1.01 → "knapp" (close/risky)
  else if (winLossDelta <= 1.01) {
    return 'WIRD_KNAPP';
  }
  // winLossDelta > 1.01 → "eingebucht" (will be assigned)
  else {
    return 'WIRD_EINGEBUCHT';
  }
};

// Normalisiert eine Telefonnummer: entfernt Leerzeichen und Bindestriche
export const normalizePhoneNumber = (phone: string): string => {
  return phone.replace(/[\s-]/g, '');
};

// Prüft ob eine Telefonnummer dem E.164-Format entspricht (mit Ländervorwahl)
// Leerer String gilt als gültig (Caller prüft Pflichtfeld separat)
export const validatePhoneE164 = (phone: string): { valid: boolean; error?: string } => {
  const normalized = normalizePhoneNumber(phone);
  if (normalized === '') {
    return { valid: true };
  }
  const phoneRegex = /^\+[1-9]\d{6,14}$/;
  if (!phoneRegex.test(normalized)) {
    return {
      valid: false,
      error: 'Telefonnummer muss mit Ländervorwahl beginnen, z. B. +491701234567'
    };
  }
  return { valid: true };
};

// SCN (Simple Cloud Notifications) validation helpers
// SCN UID must start with "USR" (e.g., "USR123456")
export const validateSCNUID = (scnUID: string): { valid: boolean; error?: string } => {
  if (!scnUID || scnUID.trim() === '') {
    return { valid: false, error: 'SCN UID darf nicht leer sein' };
  }

  if (!scnUID.startsWith('USR')) {
    return { valid: false, error: 'SCN UID muss mit "USR" beginnen' };
  }

  return { valid: true };
};

// SCN Token must be exactly 64 alphanumeric characters
export const validateSCNToken = (scnToken: string): { valid: boolean; error?: string } => {
  if (!scnToken || scnToken.trim() === '') {
    return { valid: false, error: 'SCN Token darf nicht leer sein' };
  }

  const tokenRegex = /^[A-Za-z0-9]{64}$/;
  if (!tokenRegex.test(scnToken)) {
    return { valid: false, error: 'SCN Token muss genau 64 alphanumerische Zeichen enthalten' };
  }

  return { valid: true };
};

// Helper to mask SCN Token for display (shows first 8 and last 8 characters)
export const maskSCNToken = (scnToken: string | null): string => {
  if (!scnToken) {
    return 'Nicht konfiguriert';
  }

  if (scnToken.length < 16) {
    return '***';
  }

  return `${scnToken.substring(0, 8)}...${scnToken.substring(scnToken.length - 8)}`;
};

export function never(v: never): never { return v; }
